import { Injectable, OnDestroy } from '@angular/core';
import { OauthService } from 'mnm-webapp';
import { environment } from '@masar/env/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import {
    createFeatureStore,
    createLicenseFeatureLoader,
    evaluateRouteAccess,
    hasAnyFeature as hasAnyCerebroFeature,
    hasFeature as hasCerebroFeature,
} from '@m6d/cerebro';

@Injectable()
export class LicenseFeatureService implements OnDestroy {
    private readonly featureStore = createFeatureStore({
        loadFeatures: createLicenseFeatureLoader({
            url: `${environment.apiUrl}/licenses/features`,
        }),
    });
    private readonly state = new BehaviorSubject(this.featureStore.getState());
    private readonly unsubscribeAll = new Subject<void>();
    private readonly unsubscribeStore: () => void;

    public constructor(private readonly oauthService: OauthService) {
        this.unsubscribeStore = this.featureStore.subscribe(() => {
            this.state.next(this.featureStore.getState());
        });

        this.reload();

        this.oauthService.status$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(status => {
                if (status === 'logged_in') {
                    this.reload();
                }
            });
    }

    public ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
        this.unsubscribeStore();
        this.state.complete();
    }

    public get features$(): Observable<string[]> {
        return this.state.pipe(
            filter(x => x.loaded),
            map(x => x.features)
        );
    }

    public get loaded$(): Observable<string[]> {
        return this.features$.pipe(first());
    }

    public hasFeature(name: string): boolean {
        return hasCerebroFeature(this.featureStore.getState().features, name);
    }

    public hasAnyFeature(featureIds: string[]): boolean {
        if (!featureIds || featureIds.length === 0) return true;

        return hasAnyCerebroFeature(
            this.featureStore.getState().features,
            featureIds
        );
    }

    public canAccessRoute(featureIds: string[]): boolean {
        return evaluateRouteAccess(
            { featureIds: featureIds ?? [] },
            { features: this.featureStore.getState().features }
        ).allowed;
    }

    public reload(): void {
        this.featureStore.load().catch(() => undefined);
    }
}
